/* eslint-disable import/no-named-as-default */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ButtonSecondary, Type } from '@spotify-internal/encore-creator-web';
import {
  azure,
  spacer12,
  spacer24,
  spacer32,
  screenXsMax,
} from '@spotify-internal/encore-foundation';
import { ConnectedPodcastUpdateModal } from '../PodcastUpdateModal';
import './index.module.scss';
import { logger } from '../../../lib/Api';
import { FormattedMessage } from 'react-intl';

const SOUNDTRAP_NETWORK_ID = '7JcQe06T6pI4mBEUsQNyRo';
const ANCHOR_NETWORK_ID = '320m41w1Kduwu8vcjFgFkG';

interface Props {
  licensor: string;
  networkId: string;
  feedUrl: string;
  showId: string;
  hasNetworkPermissions: boolean;
  isTaxiFeed?: boolean;
}

export function PodcastSettings(props: Props) {
  const [showModal, setShowModal] = useState(false);
  const isTaxiAnchor = props.networkId === ANCHOR_NETWORK_ID && props.isTaxiFeed;

  function handleOnClick() {
    logger.startedPodcastUpdateFlow(props.showId);
    toggleModal();
  }

  function toggleModal() {
    setShowModal(!showModal);
  }

  function RSSFeedCopy() {
    if (props.networkId === SOUNDTRAP_NETWORK_ID) return 'Provided by Soundtrap';
    if (isTaxiAnchor) return 'Provided by Anchor';
    return props.feedUrl;
  }

  return (
    <div data-qa="podcast-settings">
      <Type as="p" variant={Type.heading3}>
        <FormattedMessage id="podcast-settings.title" defaultMessage="Podcast settings" />
      </Type>
      <PodcastInfo>
        <div>
          <Type as="p" variant={Type.body1} weight={Type.bold}>
            <FormattedMessage
              id="podcast-settings.link-to-feed"
              defaultMessage="Link to RSS feed"
            />
          </Type>
          <Type as="p" variant={Type.body2} style={{ marginBottom: spacer32 }} color="gray60">
            {RSSFeedCopy()}
          </Type>
          <Type as="p" variant={Type.body1} weight={Type.bold}>
            <FormattedMessage
              id="podcast-settings.licensor"
              defaultMessage="Licensor / Hosting Provider"
            />
          </Type>
          <Type.p variant={Type.body2} color="gray60">
            {props.licensor}
          </Type.p>
        </div>
        {!isTaxiAnchor && (
          <div style={{ marginBottom: spacer12 }}>
            <ButtonSecondary onClick={handleOnClick}>
              <FormattedMessage id="podcast-settings.update" defaultMessage="Update" />
            </ButtonSecondary>
          </div>
        )}
      </PodcastInfo>
      {isTaxiAnchor && (
        <Type.p variant={Type.body2} color="gray60" style={{ marginTop: spacer32 }}>
          <FormattedMessage
            id="podcast-settings.anchor-info"
            defaultMessage="For more information about your RSS feed, check your <a>Anchor settings</a>."
            values={{
              a: (...chunks) => (
                <a
                  href="https://anchor.fm/dashboard/podcast/distribution"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: azure }}
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </Type.p>
      )}
      {showModal && (
        <ConnectedPodcastUpdateModal
          existingFeedUrl={props.feedUrl}
          showId={props.showId}
          onClose={toggleModal}
          hasNetworkPermissions={props.hasNetworkPermissions}
        />
      )}
    </div>
  );
}

PodcastSettings.propTypes = {
  licensor: PropTypes.string.isRequired,
  networkId: PropTypes.string.isRequired,
  feedUrl: PropTypes.string.isRequired,
  showId: PropTypes.string.isRequired,
  hasNetworkPermissions: PropTypes.bool.isRequired,
};

const PodcastInfo = styled.div`
  display: grid;
  grid-template-columns: 450px auto;
  align-items: flex-end;

  @media (max-width: ${screenXsMax}) {
    grid-template-columns: 1fr;
    grid-row-gap: ${spacer24};
  }
`;
